import {
  AppstoreOutlined,
  BarChartOutlined,
  DashboardOutlined,
  GoldOutlined,
  InboxOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  ShoppingOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { Button, Layout, Menu, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';

const { Sider } = Layout;
const { Text } = Typography;

interface AppSiderProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  /** Admin del tenant propio: habilita la gestion de SUS usuarios. */
  isAdmin: boolean;
  /** Admin de plataforma (PymeCode): habilita la administracion de aplicaciones/tenants. */
  isPlatformAdmin: boolean;
}

type MenuItem = Required<MenuProps>['items'][number];

function makeItem(
  label: string,
  key: string,
  icon?: ReactNode,
  children?: MenuItem[],
): MenuItem {
  return { key, label, icon, children } as MenuItem;
}

/**
 * Dumb component — sidebar navigation shell.
 * Receives collapsed state and callbacks via props.
 */
export default function AppSider({
  collapsed,
  onCollapse,
  isAdmin,
  isPlatformAdmin,
}: AppSiderProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();

  const navItems: MenuItem[] = [
    makeItem(t('navigation.dashboard'), '/dashboard', <DashboardOutlined />),
    makeItem(t('navigation.products'), '/products', <AppstoreOutlined />),
    makeItem(t('navigation.categories'), '/categories', <TagsOutlined />),
    makeItem(t('navigation.stock'), '/stock', <GoldOutlined />),
    makeItem(t('navigation.warehouses'), '/warehouses', <InboxOutlined />),
    makeItem(t('navigation.suppliers'), '/suppliers', <ShoppingOutlined />),
    makeItem(t('navigation.customers'), '/customers', <UserOutlined />),
    makeItem(t('navigation.reports'), '/reports', <BarChartOutlined />),
    // "Aplicaciones" administra tenants de TODA la plataforma: es exclusivo de
    // PymeCode, no de un Admin de tenant. "Usuarios" si corresponde al Admin del
    // tenant, que gestiona los suyos. Antes ambos colgaban del mismo isAdmin.
    ...(isAdmin
      ? [
          makeItem(t('navigation.administration'), 'admin-group', <SettingOutlined />, [
            ...(isPlatformAdmin
              ? [
                  makeItem(
                    t('navigation.applications'),
                    '/platform/applications',
                    <SettingOutlined />,
                  ),
                ]
              : []),
            makeItem(t('navigation.users'), '/users', <UsergroupAddOutlined />),
          ]),
        ]
      : []),
  ];

  /**
   * Collect all leaf keys (items without children) recursively so that both
   * top-level and submenu items can be matched against the current pathname.
   */
  function collectLeafKeys(items: MenuItem[]): string[] {
    return items.flatMap((item) => {
      if (item === null) return [];
      const typedItem = item as { key?: string; children?: MenuItem[] };
      if (typedItem.children !== undefined && typedItem.children.length > 0) {
        return collectLeafKeys(typedItem.children);
      }
      return typedItem.key !== undefined ? [typedItem.key] : [];
    });
  }

  const allLeafKeys = collectLeafKeys(navItems);

  // Determine active key by matching the start of the current pathname
  const selectedKey =
    allLeafKeys.find(
      (key) => location.pathname === key || location.pathname.startsWith(`${key}/`),
    ) ?? '/dashboard';

  // Keep the admin submenu open when on an admin child route
  const adminChildKeys = ['/platform/applications', '/users'];
  const openKeys = adminChildKeys.includes(selectedKey) ? ['admin-group'] : [];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    // Ignore clicks on group (non-route) items
    if (key === 'admin-group') return;
    void navigate(key);
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      width={220}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
      }}
      theme="dark"
    >
      {/* Logo area */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0' : '0 16px 0 20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ color: '#1677ff', fontSize: 20 }} />
            <Text strong style={{ color: '#fff', fontSize: 16 }}>
              PymeStock
            </Text>
          </div>
        )}
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => onCollapse(!collapsed)}
          style={{ color: '#fff', fontSize: 16, width: 32, height: 32 }}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          aria-expanded={!collapsed}
        />
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        defaultOpenKeys={openKeys}
        items={navItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </Sider>
  );
}
